import { AKComponent } from '@theme/component';

/**
 * BlogSummary — Sommaire d’article généré à partir des titres (h2/h3) du contenu.
 *
 * Le markup (conteneur, titre, liste) est fourni par le Liquid. Le JS remplit la liste
 * avec des liens vers les sections et gère le défilement fluide au clic.
 *
 * La liste est résolue via ref "summaryList" ou, si elle est dans un autre composant
 * (ex. accordion-component), via le sélecteur .c-blog-post__summary-list.
 *
 * @typedef {Object} BlogSummaryRefs
 * @property {HTMLUListElement} [summaryList]
 *
 * @extends {AKComponent<BlogSummaryRefs>}
 */
export class BlogSummary extends AKComponent {
  /**
   * Liste du sommaire (ref ou sélecteur de repli quand la liste est dans un accordion-component).
   * @returns {HTMLUListElement | null}
   */
  get summaryListEl() {
    return this.refs.summaryList ?? this.querySelector('.c-blog-post__summary-list');
  }

  connectedCallback() {
    super.connectedCallback();
    this.buildSummary();
    this.enableSmoothScroll();
  }

  buildSummary() {
    const articleMain = document.querySelector('.c-blog-post__content');
    const summaryListEl = this.summaryListEl;

    if (!articleMain || !summaryListEl) return;

    const headingLevel = this.dataset.headingLevel || 'lvl_1';
    const selectors = headingLevel === 'lvl_2' ? 'h2, h3' : 'h2';
    const headings = articleMain.querySelectorAll(selectors);

    headings.forEach((heading, index) => {
      if (!(heading instanceof HTMLElement)) return;

      const id = heading.id || `section-${index + 1}`;
      heading.id = id;

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${id}`;
      a.textContent = heading.textContent?.trim() ?? '';

      if (heading.tagName.toLowerCase() === 'h3') {
        li.classList.add('c-blog-post__summary-subitem');
      }

      li.appendChild(a);
      summaryListEl.appendChild(li);
    });
  }

  enableSmoothScroll() {
    const summaryListEl = this.summaryListEl;
    if (!summaryListEl) return;

    summaryListEl.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLAnchorElement)) return;

      e.preventDefault();
      const href = target.getAttribute('href');
      if (!href) return;

      const targetId = href.slice(1);
      const section = document.getElementById(targetId);
      if (section) {
        window.scrollTo({
          top: section.getBoundingClientRect().top + window.scrollY - 16,
          behavior: 'smooth'
        });
      }
    });
  }
}

if (!customElements.get('blog-summary')) {
  customElements.define('blog-summary', BlogSummary);
}
